if (!customElements.get('pb-lead-form')) {
  class PbLeadForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.initialized) return;

      this.initialized = true;
      this.status = this.querySelector('[data-lead-status]');
      this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handlePopState = this.refreshTracking.bind(this);

      this.form.addEventListener('submit', this.handleSubmit);
      this.addEventListener('change', this.handleChange);
      window.addEventListener('popstate', this.handlePopState);

      this.refreshTracking();
      this.fillProductAndConversion();
      this.syncMirrors();
      this.updateSelectPlaceholders();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('change', this.handleChange);
      window.removeEventListener('popstate', this.handlePopState);
    }

    handleChange(event) {
      if (event.target.matches('select')) this.updateSelectPlaceholders();
      this.syncMirrors();
      this.clearStatus();
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.clearStatus();
      this.refreshTracking();
      this.fillProductAndConversion();
      this.syncMirrors();

      if (!this.validateForm()) return;

      this.setSubmitting(true);
      this.setStatus(this.dataset.sendingText || 'Wird gesendet ...');

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          body: new FormData(this.form),
          credentials: 'include'
        });

        let payload = {};
        try {
          payload = await response.json();
        } catch (error) {
          payload = {};
        }

        if (!response.ok || payload.ok !== true) {
          const fallback = response.ok
            ? (this.dataset.genericError || 'Die Anfrage konnte nicht versendet werden.')
            : `Serverfehler (${response.status}). Bitte später erneut versuchen.`;
          throw new Error(payload.error || payload.message || fallback);
        }

        this.pushLeadEvent();
        await this.submitNewsletterIfNeeded();
        await this.wait(150);

        window.location.href = this.newsletterRequested()
          ? (this.dataset.newsletterThankYouUrl || this.dataset.thankYouUrl)
          : this.dataset.thankYouUrl;
      } catch (error) {
        console.error(error);
        this.setStatus(error.message || this.dataset.genericError || 'Es ist ein Fehler aufgetreten.', 'error');
      } finally {
        this.setSubmitting(false);
      }
    }

    refreshTracking() {
      try {
        window.FP_TRACKING?.persistFromUrl?.();
        window.FP_TRACKING?.fillHiddenFields?.();
      } catch (error) {}
    }

    fillProductAndConversion() {
      this.setHidden('lead_conversion_value', this.normalizeAmount(this.dataset.leadConversionValue, this.dataset.defaultLeadConversionValue || '140.00'));
      this.setHidden('lead_conversion_currency', this.normalizeCurrency(this.dataset.leadConversionCurrency || this.dataset.defaultLeadConversionCurrency || 'EUR'));

      if (!this.getHidden('product_title')) {
        const title = document.querySelector('h1.product-title')?.textContent?.trim() || '';
        this.setHidden('product_title', title);
      }
    }

    syncMirrors() {
      this.querySelectorAll('[data-lead-mirror-source]').forEach((source) => {
        const targetName = source.dataset.leadMirrorSource;
        if (!targetName) return;
        this.setHidden(targetName, this.getCheckedValue(source.name));
      });
    }

    updateSelectPlaceholders() {
      this.querySelectorAll('select').forEach((select) => {
        select.classList.toggle('is-placeholder', select.value === '');
      });
    }

    validateScope(scope) {
      if (!scope) return true;

      const invalidControl = Array.from(scope.querySelectorAll('input, select, textarea')).find((control) => {
        if (control.disabled || control.type === 'hidden') return false;
        if (control.closest('[data-lead-omnisend]')) return false;
        return typeof control.checkValidity === 'function' && !control.checkValidity();
      });

      if (invalidControl) {
        invalidControl.reportValidity?.();
        invalidControl.focus?.();
        return false;
      }

      const invalidGroup = Array.from(scope.querySelectorAll('[data-lead-required-group]')).find((group) => {
        const name = group.dataset.leadRequiredGroup;
        return name && !group.querySelector(`input[name="${this.escapeSelector(name)}"]:checked`);
      });

      if (invalidGroup) {
        const firstInput = invalidGroup.querySelector('input');
        firstInput?.setCustomValidity?.(this.dataset.requiredGroupError || 'Bitte wählen Sie mindestens eine Option aus.');
        firstInput?.reportValidity?.();
        firstInput?.setCustomValidity?.('');
        firstInput?.focus?.();
        return false;
      }

      return true;
    }

    validateForm() {
      if (!this.validateScope(this.form)) return false;

      const email = this.form.querySelector('[name="email"]')?.value?.trim() || '';
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.setStatus(this.dataset.emailError || 'Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'error');
        return false;
      }

      return true;
    }

    newsletterRequested() {
      return Boolean(this.form.querySelector('[name="newsletter_optin"]')?.checked);
    }

    async submitNewsletterIfNeeded() {
      if (!this.newsletterRequested()) return;
      this.pushNewsletterEvent();

      const root = this.querySelector('[data-lead-omnisend]');
      if (!root) return;

      const omni = await this.waitForOmnisend(root);
      if (!omni) return;

      this.setNativeValue(omni.email, this.formValue('email'));
      if (omni.first) this.setNativeValue(omni.first, this.formValue('first_name'));
      if (omni.last) this.setNativeValue(omni.last, this.formValue('last_name'));
      if (omni.company) this.setNativeValue(omni.company, this.formValue('company'));
      await this.wait(50);

      if (omni.submit) omni.submit.click();
      else if (typeof omni.form.requestSubmit === 'function') omni.form.requestSubmit();
      else omni.form.submit();
    }

    waitForOmnisend(root, timeout = 15000) {
      const find = () => {
        const form = root.querySelector('form[id$="-submit-form"]') || root.querySelector('form');
        if (!form) return null;
        const email = form.querySelector('input[name="emailField"]');
        if (!email) return null;
        return {
          form,
          email,
          first: form.querySelector('input[name="firstName"]'),
          last: form.querySelector('input[name="lastName"]'),
          company: form.querySelector('input[name="custom_Company"]'),
          submit: form.querySelector('button[type="submit"], input[type="submit"]')
        };
      };

      const immediate = find();
      if (immediate) return Promise.resolve(immediate);

      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          const found = find();
          if (!found) return;
          observer.disconnect();
          resolve(found);
        });

        observer.observe(root, { childList: true, subtree: true });
        window.setTimeout(() => {
          observer.disconnect();
          resolve(find());
        }, timeout);
      });
    }

    setNativeValue(element, value) {
      if (!element) return;
      const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), 'value');
      if (descriptor?.set) descriptor.set.call(element, value ?? '');
      else element.value = value ?? '';
      element.dispatchEvent(new Event('input', { bubbles: true }));
      element.dispatchEvent(new Event('change', { bubbles: true }));
      element.dispatchEvent(new Event('blur', { bubbles: true }));
    }

    pushLeadEvent() {
      try {
        const value = Number((this.getHidden('lead_conversion_value') || '').replace(',', '.'));
        const currency = (this.getHidden('lead_conversion_currency') || this.dataset.defaultLeadConversionCurrency || 'EUR').trim().toUpperCase();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_submit_lead',
          form_name: this.dataset.formName || this.formValue('form_key') || 'Lead Form',
          product_title: this.getHidden('product_title'),
          lead_conversion_value: Number.isFinite(value) ? value : Number(this.dataset.defaultLeadConversionValue || 140),
          lead_conversion_currency: currency || 'EUR'
        });
      } catch (error) {
        console.warn('dataLayer push lead failed:', error);
      }
    }

    pushNewsletterEvent() {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'newsletter_optin_lead_form',
          form_name: this.dataset.formName || this.formValue('form_key') || 'Lead Form',
          product_title: this.getHidden('product_title')
        });
      } catch (error) {
        console.warn('dataLayer push newsletter failed:', error);
      }
    }

    formValue(name) {
      return this.form.querySelector(`[name="${this.escapeSelector(name)}"]`)?.value?.trim() || '';
    }

    getHidden(name) {
      return this.form.querySelector(`input[type="hidden"][name="${this.escapeSelector(name)}"]`)?.value?.trim() || '';
    }

    setHidden(name, value) {
      const element = this.form.querySelector(`input[type="hidden"][name="${this.escapeSelector(name)}"]`);
      if (element) element.value = value ?? '';
    }

    getCheckedValue(name) {
      const checked = this.form.querySelector(`input[name="${this.escapeSelector(name)}"]:checked`);
      return checked?.value || '';
    }

    escapeSelector(value) {
      if (window.CSS?.escape) return CSS.escape(value);
      return String(value).replace(/["\\]/g, '\\$&');
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value ?? '';
      return div.innerHTML;
    }

    setSubmitting(isSubmitting) {
      this.submitButtons.forEach((button) => {
        button.disabled = isSubmitting;
      });
    }

    setStatus(message = '', type = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', type === 'error');
      this.status.classList.toggle('is-success', type === 'success');
    }

    clearStatus() {
      this.setStatus('');
    }

    normalizeAmount(value, fallback) {
      let normalized = String(value ?? '').trim();
      if (!normalized) return fallback;

      normalized = normalized.replace(/[\s\u00A0]/g, '').replace(/[^0-9,.\-]/g, '');
      const hasDot = normalized.includes('.');
      const hasComma = normalized.includes(',');

      if (hasDot && hasComma) {
        const lastDot = normalized.lastIndexOf('.');
        const lastComma = normalized.lastIndexOf(',');
        normalized = lastComma > lastDot
          ? normalized.replace(/\./g, '').replace(',', '.')
          : normalized.replace(/,/g, '');
      } else if (hasComma && !hasDot) {
        normalized = normalized.replace(',', '.');
      }

      const number = Number(normalized);
      return Number.isFinite(number) && number > 0 ? number.toFixed(2) : fallback;
    }

    normalizeCurrency(value) {
      const normalized = String(value ?? '').trim().toUpperCase().replace(/[^A-Z]/g, '');
      return normalized ? normalized.slice(0, 3) : 'EUR';
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }
  }

  customElements.define('pb-lead-form', PbLeadForm);
}
